import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, List, Empty, Spin, Typography, Space } from 'antd';
import { PictureOutlined, BookOutlined, UserOutlined, RightOutlined, LinkOutlined } from '@ant-design/icons';
import { albumAPI, blogAPI, userAPI } from '../utils/api';
import { IMG_URL } from '../utils/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { Text } = Typography;

const Default = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    userCount: 0,
    albumCount: 0,
    blogCount: 0
  });
  const [latestAlbums, setLatestAlbums] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [hotAlbums, setHotAlbums] = useState([]);
  const [hotBlogs, setHotBlogs] = useState([]);

  // 友情链接
  const friendLinks = [
    { name: '寒木春华个人网站', url: 'https://www.lijianc.com', description: '个人网站' },
    { name: '飞牛音乐', url: 'https://y.rtycn.com', description: '音乐网站' },
    { name: '汽车标准件手册', url: 'https://www.cnsph.com', description: '汽车标准件手册、常用国家标准' },
    { name: 'CATIA汽车标准件下载', url: 'https://down.cnsph.com', description: 'CATIA版本汽车标准件下载' },
    { name: '常用标准查询', url: 'https://www.cnsph.com/standard.aspx', description: '机械与汽车相关常用标准查询' }
  ];

  // 设置网页标题
  useDocumentTitle('rtycn - 首页');

  useEffect(() => {
    fetchStats();
    fetchLatestAlbums();
    fetchLatestBlogs();
    fetchHotAlbums();
    fetchHotBlogs();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, albumsRes, blogsRes] = await Promise.all([
        userAPI.getAllUsers(),
        albumAPI.getAlbums({ limit: 1000 }),
        blogAPI.getBlogs({ limit: 1000 })
      ]);

      setStats({
        userCount: usersRes.data?.length || 0,
        albumCount: albumsRes.data?.length || 0,
        blogCount: blogsRes.data?.length || 0
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestAlbums = async () => {
    try {
      const response = await albumAPI.getLatestAlbums(5);
      setLatestAlbums(response.data || []);
    } catch (error) {
      console.error('获取最新相册失败:', error);
      setLatestAlbums([]);
    }
  };

  const fetchLatestBlogs = async () => {
    try {
      const response = await blogAPI.getLatestBlogs(5);
      setLatestBlogs(response.data || []);
    } catch (error) {
      console.error('获取最新博客失败:', error);
      setLatestBlogs([]);
    }
  };

  const fetchHotAlbums = async () => {
    try {
      const response = await albumAPI.getHotAlbums(5);
      setHotAlbums(response.data || []);
    } catch (error) {
      console.error('获取热门相册失败:', error);
      setHotAlbums([]);
    }
  };

  const fetchHotBlogs = async () => {
    try {
      const response = await blogAPI.getHotBlogs(5);
      setHotBlogs(response.data || []);
    } catch (error) {
      console.error('获取热门博客失败:', error);
      setHotBlogs([]);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h3>欢迎进入rtycn博客系统</h3>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="用户总数"
              value={stats.userCount}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="相册总数"
              value={stats.albumCount}
              prefix={<PictureOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="博客总数"
              value={stats.blogCount}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card title="最新相册">
            {latestAlbums.length === 0 ? (
              <Empty description="暂无相册" />
            ) : (
              <List
                dataSource={latestAlbums}
                renderItem={(album) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${album.authorId}/album?id=${album.id}`}>
                        查看 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <img
                          src={album.coverImage ? `${IMG_URL}${album.coverImage}` : '/img/default-album.png'}
                          alt={album.title}
                          style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        />
                      }
                      title={album.title}
                      description={`${album.authorId} · ${album.photoCount || 0} 张照片`}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="最新博客">
            {latestBlogs.length === 0 ? (
              <Empty description="暂无博客" />
            ) : (
              <List
                dataSource={latestBlogs}
                renderItem={(blog) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${blog.authorId}/blog/article/?id=${blog.id}`}>
                        阅读 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      title={blog.title}
                      description={
                        <div>
                          <div>{blog.summary}</div>
                          <div style={{ color: '#999', fontSize: '12px', marginTop: 4 }}>
                            {blog.authorId} · {blog.viewCount || 0} 次浏览
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12}>
          <Card title="热门相册">
            {hotAlbums.length === 0 ? (
              <Empty description="暂无相册" />
            ) : (
              <List
                dataSource={hotAlbums}
                renderItem={(album) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${album.authorId}/album/?id=${album.id}`}>
                        查看 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <img
                          src={album.coverImage ? `${IMG_URL}${album.coverImage}` : '/img/default-album.png'}
                          alt={album.title}
                          style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        />
                      }
                      title={album.title}
                      description={`${album.authorId} · ${album.viewCount || 0} 次浏览`}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="热门博客">
            {hotBlogs.length === 0 ? (
              <Empty description="暂无博客" />
            ) : (
              <List
                dataSource={hotBlogs}
                renderItem={(blog) => (
                  <List.Item
                    actions={[
                      <a onClick={() => window.location.href = `/${blog.authorId}/blog/article/?id=${blog.id}`}>
                        阅读 <RightOutlined />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      title={blog.title}
                      description={
                        <div>
                          <div>{blog.summary}</div>
                          <div style={{ color: '#999', fontSize: '12px', marginTop: 4 }}>
                            {blog.authorId} · {blog.viewCount || 0} 次浏览
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 友情链接 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card
            title={
              <Space>
                <LinkOutlined />
                <span>友情链接</span>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {friendLinks.map((link, index) => (
                <Col key={index} xs={12} sm={8} md={6}>
                  <Card
                    hoverable
                    onClick={() => window.open(link.url, '_blank')}
                    style={{ cursor: 'pointer', textAlign: 'center' }}
                  >
                    <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                      {link.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {link.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Default;
